import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { format, parseISO, differenceInDays, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MapPin, Calendar, Users, ChevronRight, Crown, Vote } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const statusConfig = {
  planning: { label: 'Planejando', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  voting: { label: 'Votando', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  confirmed: { label: 'Confirmada', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  in_progress: { label: 'Em andamento', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'Concluída', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const defaultImages = [
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1530789253388-582c481c54b0?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=800&q=80',
];

export const TripCard = ({ trip, index = 0, viewMode = 'grid-2' }) => {
  const isVotingMode = trip.status === 'voting';
  const startDate = trip.start_date ? (typeof trip.start_date === 'string' ? parseISO(trip.start_date) : trip.start_date) : null;
  const endDate = trip.end_date ? (typeof trip.end_date === 'string' ? parseISO(trip.end_date) : trip.end_date) : null;
  const now = new Date();
  const daysUntil = startDate ? differenceInDays(startDate, now) : null;
  const isUpcoming = startDate ? isBefore(now, startDate) : false;
  
  const status = statusConfig[trip.status] || statusConfig.planning;
  const coverImage = trip.cover_image || defaultImages[index % defaultImages.length];

  // List mode - horizontal layout
  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
      >
        <Link to={`/trip/${trip.id}`} data-testid={`trip-card-${trip.id}`}>
          <Card className="group overflow-hidden hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5 border-slate-100 dark:border-slate-700">
            <div className="flex">
              {/* Cover Image - smaller in list mode */}
              <div className="relative w-32 sm:w-40 h-24 sm:h-28 overflow-hidden flex-shrink-0">
                <img
                  src={coverImage}
                  alt={trip.destination}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
              </div>

              {/* Content */}
              <div className="flex-1 p-3 sm:p-4 flex items-center">
                <div className="flex-1 min-w-0">
                  {/* Title & Status */}
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-bold text-base sm:text-lg text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors truncate">
                      {trip.name}
                    </h3>
                    {trip.my_role === 'organizer' && (
                      <Crown className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    )}
                    <Badge className={`${status.color} text-xs flex-shrink-0`}>
                      {status.label}
                    </Badge>
                  </div>

                  {/* Destination */}
                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 mb-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="text-sm truncate">
                      {trip.origin ? `${trip.origin} → ${trip.destination}` : trip.destination}
                    </span>
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center gap-4 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-1">
                      {startDate && endDate ? (
                        <>
                          <Calendar className="w-3 h-3" />
                          <span>
                            {format(startDate, "dd/MM", { locale: ptBR })} - {format(endDate, "dd/MM", { locale: ptBR })}
                          </span>
                        </>
                      ) : (
                        <>
                          <Vote className="w-3 h-3 text-purple-500" />
                          <span className="text-purple-600 dark:text-purple-400">A definir</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{trip.member_count}</span>
                    </div>
                    {isUpcoming && daysUntil !== null && daysUntil >= 0 && (
                      <span className="text-orange-600 dark:text-orange-400 font-medium">
                        {daysUntil === 0 ? 'Hoje!' : `Em ${daysUntil}d`}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
              </div>
            </div>
          </Card>
        </Link>
      </motion.div>
    );
  }

  // Grid mode (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <Link to={`/trip/${trip.id}`} data-testid={`trip-card-${trip.id}`} className="h-full block">
        <Card className="group overflow-hidden hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-slate-100 dark:border-slate-700 h-full flex flex-col">
          {/* Cover Image */}
          <div className="relative h-32 sm:h-36 overflow-hidden flex-shrink-0">
            <img
              src={coverImage}
              alt={trip.destination}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            
            {/* Status Badge */}
            <Badge className={`absolute top-2 right-2 text-xs ${status.color}`}>
              {status.label}
            </Badge>
            
            {/* Days Counter */}
            {isUpcoming && daysUntil !== null && daysUntil >= 0 && (
              <div className="absolute bottom-2 left-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-lg px-2 py-0.5">
                <span className="text-xs font-medium text-slate-900 dark:text-white">
                  {daysUntil === 0 ? 'Hoje!' : `Em ${daysUntil}d`}
                </span>
              </div>
            )}
            {/* Voting indicator */}
            {isVotingMode && (
              <div className="absolute bottom-2 left-2 bg-purple-500/90 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1">
                <Vote className="w-3 h-3 text-white" />
                <span className="text-xs font-medium text-white">Vote!</span>
              </div>
            )}
          </div>

          <CardContent className="p-3 flex flex-col flex-1">
            {/* Title & Role */}
            <div className="flex items-start justify-between gap-1 mb-1">
              <h3 className="font-heading font-bold text-sm sm:text-base text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-1">
                {trip.name}
              </h3>
              {trip.my_role === 'organizer' && (
                <Crown className="w-4 h-4 text-orange-500 flex-shrink-0" />
              )}
            </div>

            {/* Destination */}
            <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300 mb-2">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="text-xs sm:text-sm truncate">
                {trip.origin ? `${trip.origin} → ${trip.destination}` : trip.destination}
              </span>
            </div>

            {/* Info Row - pushed to bottom */}
            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mt-auto">
              <div className="flex items-center gap-1">
                {startDate && endDate ? (
                  <>
                    <Calendar className="w-3 h-3" />
                    <span>
                      {format(startDate, "dd/MM", { locale: ptBR })}
                    </span>
                  </>
                ) : (
                  <>
                    <Vote className="w-3 h-3 text-purple-500" />
                    <span className="text-purple-600 dark:text-purple-400">A definir</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span>{trip.member_count}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
};

export default TripCard;
